import { redactPiiFromSentryEvent } from '@flowsoft/observability';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN;
const SENTRY_ENVIRONMENT =
  process.env.EXPO_PUBLIC_ENV ?? process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ?? 'development';
const SENTRY_ENABLED = process.env.EXPO_PUBLIC_SENTRY_ENABLED !== 'false';

function tracesSampleRateForEnv(environment: string): number {
  if (environment === 'production') return 0.2;
  if (environment === 'preview') return 0.5;
  return 1;
}

const isExpoGo = Constants.executionEnvironment === 'storeClient';

const sentryStub = {
  init: () => {},
  setContext: () => {},
  setUser: () => {},
  addBreadcrumb: () => {},
  captureException: () => {},
  captureMessage: () => {},
  withScope: (cb: (scope: any) => void) => {
    cb({ setContext: () => {}, setLevel: () => {}, setTag: () => {} });
  },
  wrap: <T>(component: T): T => component,
  browserTracingIntegration: () => ({}),
  reactNativeTracingIntegration: () => ({}),
  reactNavigationIntegration: () => ({ registerNavigationContainer: () => {} }),
};

function loadSentry(): any {
  if (isExpoGo || !SENTRY_ENABLED) {
    return sentryStub;
  }

  if (Platform.OS === 'web') {
    try {
      const browser = require('@sentry/react');
      // @sentry/react has no `wrap` (it's a @sentry/react-native API). Backfill
      // an identity wrap so callers can use Sentry.wrap() uniformly across platforms.
      // Use a prototype-backed object so we don't mutate the (possibly frozen) module namespace.
      if (typeof browser.wrap !== 'function') {
        const wrapped = Object.create(browser);
        wrapped.wrap = <T>(component: T): T => component;
        return wrapped;
      }
      return browser;
    } catch (error) {
      console.warn('Sentry browser module not available', error);
      return sentryStub;
    }
  }

  try {
    return require('@sentry/react-native');
  } catch (error) {
    console.warn('Sentry native module not available', error);
    return sentryStub;
  }
}

const Sentry: any = loadSentry();

let navigationIntegration: { registerNavigationContainer?: (ref: unknown) => void } | null = null;

export function registerNavigationContainer(ref: unknown): void {
  navigationIntegration?.registerNavigationContainer?.(ref);
}

function beforeSend(event: any, hint: any) {
  const error = hint?.originalException;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String(error.message).toLowerCase();
    if (message.includes('network request failed')) {
      return null;
    }
  }

  return redactPiiFromSentryEvent(event);
}

type SentryIntegrationName =
  | 'browserTracingIntegration'
  | 'reactNativeTracingIntegration'
  | 'reactNavigationIntegration';

interface ResolvedSentryOptions {
  config: Record<string, unknown>;
  integrationNames: SentryIntegrationName[];
}

export function resolveSentryOptions(
  platform: string,
  environment: string,
  options: { dsn?: string; release?: string } = {},
): ResolvedSentryOptions {
  const config: Record<string, unknown> = {
    dsn: options.dsn,
    environment,
    debug: environment === 'development',
    tracesSampleRate: tracesSampleRateForEnv(environment),
    beforeSend,
  };

  if (options.release) {
    config.release = options.release;
  }

  if (platform === 'web') {
    return { config, integrationNames: ['browserTracingIntegration'] };
  }

  config.enableInExpoDevelopment = false;
  config.tracePropagationTargets = ['localhost', /^\//];

  return {
    config,
    integrationNames: ['reactNativeTracingIntegration', 'reactNavigationIntegration'],
  };
}

export function initializeSentry() {
  if (isExpoGo || !SENTRY_ENABLED || !SENTRY_DSN) {
    const reason = isExpoGo
      ? 'running in Expo Go (native SDK unavailable)'
      : !SENTRY_ENABLED
        ? 'EXPO_PUBLIC_SENTRY_ENABLED=false'
        : 'EXPO_PUBLIC_SENTRY_DSN is missing';
    console.warn(`[Sentry] NOT initialized — ${reason}. Errors will not be reported.`);
    return;
  }

  try {
    const appVersion = Constants.expoConfig?.version || 'unknown';
    const sdkName = Platform.OS === 'web' ? '@sentry/react' : '@sentry/react-native';
    const commitSha: string | undefined = Constants.expoConfig?.extra?.commitSha;

    if (!commitSha) {
      console.warn(
        '[Sentry] Missing commit SHA — release tracking disabled. Set MOBILE_COMMIT_SHA at build time via app.config.js extra.commitSha.',
      );
    }

    const { config, integrationNames } = resolveSentryOptions(Platform.OS, SENTRY_ENVIRONMENT, {
      dsn: SENTRY_DSN,
      release: commitSha,
    });

    const integrations = [];
    for (const name of integrationNames) {
      if (name === 'reactNavigationIntegration') {
        navigationIntegration =
          typeof Sentry.reactNavigationIntegration === 'function'
            ? Sentry.reactNavigationIntegration()
            : null;
        if (navigationIntegration) {
          integrations.push(navigationIntegration);
        }
        continue;
      }
      if (typeof Sentry[name] === 'function') {
        integrations.push(Sentry[name]());
      }
    }

    Sentry.init({ ...config, integrations });

    Sentry.setContext('device', {
      platform: Platform.OS,
      version: Platform.Version,
      isDevice: Constants.isDevice,
    });

    Sentry.setContext('app', {
      version: appVersion,
      buildNumber:
        Constants.expoConfig?.ios?.buildNumber ||
        Constants.expoConfig?.android?.versionCode ||
        'unknown',
    });

    Sentry.addBreadcrumb({
      category: 'app.lifecycle',
      message: 'Sentry initialized',
      level: 'info',
      data: {
        platform: Platform.OS,
        environment: SENTRY_ENVIRONMENT,
        appVersion,
        sdk: sdkName,
      },
    });

    console.log(
      `Sentry initialized — sdk: ${sdkName}, platform: ${Platform.OS}, version: ${appVersion}, env: ${SENTRY_ENVIRONMENT}`,
    );
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
}

export { Sentry };
